import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "../../../lib/auth";
import { savedStaysApi } from "../../account/api/saved-stays.api";
import { accountKeys } from "../../account/constants/query-keys";

/**
 * Saved hotels, riads and villas (owner, 2026-09-29: "add saving for hotels
 * too"). Stays are not activities, so they have their own list: the
 * activity hearts write `wishlists`, which can only point at an activity and
 * which the mobile app reads as it is. This is the same shape as
 * SavedProvider next door: the browser holds the hearts while signed out,
 * and on sign-in they move to the account and the browser forgets them.
 */

export const SAVED_STAYS_KEY = "mj-saved-stays";
const CHANGED_EVENT = "mj-saved-stays-changed";

/** Enough to draw the card on /account without asking the server. */
export interface LocalSavedStay {
  id: string;
  slug: string;
  citySlug: string;
  name: string;
  cityName?: string;
  image?: string;
  propertyType?: string;
  starRating?: number;
  savedAt: number;
}

export type SavedStayInput = Omit<LocalSavedStay, "savedAt">;

function isLocalSavedStay(x: unknown): x is LocalSavedStay {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.slug === "string" &&
    typeof o.citySlug === "string" &&
    typeof o.name === "string" &&
    typeof o.savedAt === "number"
  );
}

export function readSavedStays(): LocalSavedStay[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(SAVED_STAYS_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter(isLocalSavedStay) : [];
  } catch {
    return [];
  }
}

function writeSavedStays(items: LocalSavedStay[]): void {
  try {
    if (items.length === 0) window.localStorage.removeItem(SAVED_STAYS_KEY);
    else window.localStorage.setItem(SAVED_STAYS_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT));
  } catch {
    /* Storage full or blocked: the heart lasts until the page is left. */
  }
}

/** Flips one stay in the browser's list; returns whether it is now saved. */
function toggleLocal(item: SavedStayInput): boolean {
  const list = readSavedStays();
  const i = list.findIndex((s) => s.id === item.id);
  if (i >= 0) {
    list.splice(i, 1);
    writeSavedStays(list);
    return false;
  }
  writeSavedStays([{ ...item, savedAt: Date.now() }, ...list]);
  return true;
}

/** The browser's saved stays, live: for /account while signed out. */
export function useLocalSavedStays(): LocalSavedStay[] {
  const [items, setItems] = useState<LocalSavedStay[]>([]);
  useEffect(() => {
    const sync = () => setItems(readSavedStays());
    sync();
    const onStorage = (e: StorageEvent) => {
      if (e.key === SAVED_STAYS_KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGED_EVENT, sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGED_EVENT, sync);
    };
  }, []);
  return items;
}

interface SavedStaysValue {
  isSaved: (id: string) => boolean;
  /** Returns the new state, so a caller can drive its own icon. */
  toggle: (item: SavedStayInput) => Promise<boolean>;
}

const SavedStaysContext = createContext<SavedStaysValue | null>(null);

export function SavedStaysProvider({ children }: { children: ReactNode }) {
  const session = useSession() as { data?: { user?: { id?: string } } | null };
  const userId = session?.data?.user?.id ?? null;
  const queryClient = useQueryClient();
  const [ids, setIds] = useState<Set<string>>(new Set());
  const mergedFor = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const local = readSavedStays();
      if (!userId) {
        mergedFor.current = null;
        setIds(new Set(local.map((s) => s.id)));
        return;
      }
      try {
        if (mergedFor.current !== userId && local.length > 0) {
          await savedStaysApi.sync(local.map((s) => s.id));
          if (cancelled) return;
          // Cleared only once the account has them: a failed merge leaves the
          // hearts where they were rather than losing them.
          writeSavedStays([]);
        }
        mergedFor.current = userId;
        const items = await savedStaysApi.listMine();
        if (cancelled) return;
        setIds(new Set(items.map((s) => s.id)));
        void queryClient.invalidateQueries({ queryKey: accountKeys.savedStays() });
      } catch {
        if (!cancelled) setIds(new Set(local.map((s) => s.id)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, queryClient]);

  const toggle = useCallback(
    async (item: SavedStayInput) => {
      const was = ids.has(item.id);
      const next = new Set(ids);
      if (was) next.delete(item.id);
      else next.add(item.id);
      setIds(next);
      if (!userId) return toggleLocal(item);
      try {
        if (was) await savedStaysApi.remove(item.id);
        else await savedStaysApi.add(item.id);
        void queryClient.invalidateQueries({ queryKey: accountKeys.savedStays() });
        return !was;
      } catch {
        setIds(ids);
        return was;
      }
    },
    [ids, userId, queryClient],
  );

  const value = useMemo<SavedStaysValue>(() => ({ isSaved: (id) => ids.has(id), toggle }), [ids, toggle]);
  return <SavedStaysContext.Provider value={value}>{children}</SavedStaysContext.Provider>;
}

/** Outside the provider (a test, a lab) the browser list stands in. */
export function useSavedStays(): SavedStaysValue {
  const ctx = useContext(SavedStaysContext);
  const local = useLocalSavedStays();
  const fallback = useMemo<SavedStaysValue>(
    () => ({ isSaved: (id) => local.some((s) => s.id === id), toggle: async (item) => toggleLocal(item) }),
    [local],
  );
  return ctx ?? fallback;
}
